import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { compileDeclareNgModuleFromMetadata } from '@angular/compiler';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { EncryptService } from './encrypt.service';
import { navbarData } from '../shared/json/nav-data';
import { TokenService } from './token.service';
import ciudades from '../shared/json/ciudades';

interface seccion {
  codSeccion?: Number;
  routeLink: String;
  icon: String;
  label: String;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private accesoData: any;

  constructor(
    private encryptSvc: EncryptService,
    private http: HttpClient,
    private tknSvc: TokenService
  ) {
    this.accesoData = null;
  }

  getAcceso(): Promise<any> {
    if (this.accesoData !== null) {
      return Promise.resolve(this.accesoData);
    }
    const token = this.tknSvc.getToken() || '';
    if (token === '') {
      return new Promise((resolve, reject) => {
        resolve(null);
      });
    }
    const headers = new HttpHeaders({ token: token });
    return new Promise((resolve, reject) => {
      this.http
        .get('/cgi-bin/wspd_cgi.sh/WService=api/queryAccesos.r', {
          headers: headers,
        })
        .subscribe({
          next: (data: any) => {
            this.accesoData = data;
            resolve(data);
          },
          error: (err) => reject(err),
        });
    });
  }

  async getSecciones() {
    if (this.accesoData === null) {
      await this.getAcceso();
    }
    const res: any = this.accesoData;
    const dataSeccion = await res.dsAccesos.ttSecciones;
    // si el usuario no tiene permisos a ninguna seccion, devuelvo solo el home
    if (
      dataSeccion === undefined ||
      dataSeccion.length == 0 ||
      dataSeccion == null
    ) {
      return navbarData.filter((item: any) => item.codSeccion === 2);
    }

    // si el usuario tiene permisos a todas las secciones estándar (codSeccion=1),
    // devuelvo las secciones estándar (< 1000) + las que tenga asignadas explícitamente (>= 1000)
    if (dataSeccion.find((item: any) => item.codSeccion === 1)) {
      const explicitas = dataSeccion
        .filter((s: any) => s.codSeccion >= 1000)
        .map((s: any) => s.codSeccion);
      return navbarData.filter(
        (item: any) => item.codSeccion < 1000 || explicitas.includes(item.codSeccion)
      );
    }

    // si el usuario tiene permisos a algunas secciones, devuelvo las secciones que tiene permisos
    const secciones: any[] = [];
    dataSeccion.map((seccion: any) => {
      const seccionData = navbarData.find(
        (item: any) => item.codSeccion === seccion.codSeccion
      );
      // una seccion que el front no conoce (ej. nueva en la base) se ignora en vez de romper el menu
      if (seccionData) secciones.push(seccionData);
    });
    // si ninguna seccion es conocida por el front, mismo comportamiento que sin permisos: solo el home
    return secciones.length
      ? secciones
      : navbarData.filter((item: any) => item.codSeccion === 2);
  }

  /**
   * Devuelve todos los recursos de todas las secciones
   */
  async getRecursos() {
    if (this.accesoData === null) {
      await this.getAcceso();
    }
    const res: any = this.accesoData;
    const dataSeccion = res.dsAccesos.ttSecciones;

    if (!dataSeccion || dataSeccion.length === 0) {
      return [];
    }

    // aplanamos ttRecursos de cada sección y le agregamos el codSeccion
    const recursos: any[] = [];
    dataSeccion.forEach((seccion: any) => {
      if (Array.isArray(seccion.ttRecursos)) {
        seccion.ttRecursos.forEach((recurso: any) => {
          recursos.push({
            ...recurso,
            codSeccion: seccion.codSeccion,
          });
        });
      }
    });

    return recursos;
  }

  /**
   * Devuelve los recursos de una sección por su código
   * (por ejemplo, codSeccion = 3 para "cotizaciones")
   */
  async getRecursosPorSeccion(codSeccion: number) {
    if (this.accesoData === null) {
      await this.getAcceso();
    }
    const res: any = this.accesoData;
    const dataSeccion = res.dsAccesos.ttSecciones;

    if (!dataSeccion || dataSeccion.length === 0) {
      return [];
    }

    const seccion = dataSeccion.find((s: any) => s.codSeccion === codSeccion);

    if (!seccion || !Array.isArray(seccion.ttRecursos)) {
      return [];
    }

    return seccion.ttRecursos;
  }

  async getPermisos() {
    const res: any = await this.getAcceso();
    const permisos: any = res.dsAccesos.ttPermisos;
    if (permisos === undefined || permisos.length == 0 || permisos == null) {
      return null;
    }
    return permisos;
  }

  limpioPermisos() {
    this.accesoData = null;
  }

  // queryAccesos only fills Perfil when the user is a producer (internal users come empty)
  esProductor(usuario: any): boolean {
    return !!String(usuario?.Perfil ?? usuario?.perfil ?? '').trim();
  }

  // Profile fields that must be filled before using the extranet: email for everyone, and the
  // address for producers (Contaduría needs the postal code to load the invoice).
  // Returns the labels of the missing ones.
  datosFaltantes(usuario: any): string[] {
    if (!usuario) return [];
    const vacio = (v: any) => !String(v ?? '').trim();
    if (!this.esProductor(usuario)) return vacio(usuario.email) ? ['Email'] : [];
    return [
      vacio(usuario.email) && 'Email',
      vacio(usuario.domicilio) && 'Calle',
      vacio(usuario.domicilio_nro) && 'Número',
      !(usuario.codCiudad > 0) && 'Ciudad',
      // The city already carries a postal code; only flag it when neither is available
      vacio(usuario.cod_postal) && !ciudades.find((c) => c.codCiudad === usuario.codCiudad)?.CP && 'Código Postal',
    ].filter(Boolean) as string[];
  }

  async getUsuario() {
    const res: any = await this.getAcceso();
    const usuario: any = res.dsAccesos.ttUsuarios;
    if (usuario === undefined || usuario.length == 0 || usuario == null) {
      return null;
    }
    return usuario[0];
  }
}
